import gql from 'graphql-tag';
import { useQuery } from '@vue/apollo-composable';
import dateUtils from '@utils/date.utils';
import { useRoute } from 'vue-router';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import cookieUtils from '@utils/cookie.utils';
import baseConfig from '@config/base.config';
import { ref } from 'vue';

export interface QueryHeatMapUrlListInput {
    startTime?: string;
    endTime?: string;
    profileId?: string;
    engageId?: string;
    versionId?: string;
    searchItem?: string;
    searchAmount?: number;
    pageGroupId?: string;
    urlType?: 'original' | 'combined';
    timeZone?: string;
    id?: string;
}
export type TopHeatmapListResult = {
    pv?: number;
    url?: string;
    pid?: string;
    view?: string;
};

const QUERY = gql`
    query getPtiHeatMapUrlList($input: HeatMapUrlListInput!) {
        getPtiHeatMapUrlList(input: $input) {
            url
            pv
            pid
        }
    }
`;

const QUERY_FREE_HEATMAP_URL = gql`
    query getFreeProfileHeatMapUrl($profileId: String!) {
        getFreeProfileHeatMapUrl(profileId: $profileId)
    }
`;

const topHeatmapList: any = ref([]);
const loading = ref(false);
const freeHeatmapUrl = ref(''); // 免费用户设置的热图url
const freeHeatmapFetchLoading = ref(false);
export function useTopHeatmapList() {
    const route = useRoute();
    const { profileInfo } = useProfileInfo();

    const getTopHeatmapList = function (
        params?: QueryHeatMapUrlListInput,
        resolveCallback?: () => void,
        rejectCallback?: () => void
    ) {
        const startTime = dateUtils.getMomentTime({
            num: -6,
            type: 'day',
            isStart: true,
            timezone: params?.timeZone || profileInfo.value?.timeZone
        } as any);
        const endTime = dateUtils.getMomentTime({
            num: 0,
            type: 'day',
            isStart: true,
            timezone: params?.timeZone || profileInfo.value?.timeZone
        } as any);
        const limit = params?.searchAmount || 20;
        loading.value = true;
        const { onError: onTopHeatmapError, onResult: onTopHeatmapResult } = useQuery<{
            getPtiHeatMapUrlList: QueryHeatMapUrlListInput;
        }>(
            QUERY,
            {
                input: {
                    profileId:
                        params?.profileId ||
                        route.params?.sid ||
                        cookieUtils.getItem(baseConfig.LAST_PROFILE_ID),
                    startTime: params?.startTime || startTime,
                    endTime: params?.endTime || endTime,
                    searchAmount: limit,
                    searchItem: params?.searchItem || '',
                    urlType: params?.urlType || 'original'
                }
            },
            {
                fetchPolicy: 'cache-and-network'
            }
        );

        onTopHeatmapResult(data => {
            if (data?.data?.getPtiHeatMapUrlList) {
                topHeatmapList.value = data?.data?.getPtiHeatMapUrlList;
                resolveCallback && typeof resolveCallback === 'function' && resolveCallback();
                loading.value = false;
            }
        });
        onTopHeatmapError(err => {
            topHeatmapList.value = [];
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback();
            loading.value = false;
        });
    };

    const getFreeHeatmapUrl = function (
        profileId?: string,
        resolveCallback?: (res: any) => void,
        rejectCallback?: () => void
    ) {
        freeHeatmapFetchLoading.value = true;
        const { onError: onFreeHeatmapError, onResult: onFreeHeatmapResult } = useQuery<{
            getFreeProfileHeatMapUrl: string;
        }>(
            QUERY_FREE_HEATMAP_URL,
            {
                profileId:
                    profileId ||
                    route.params?.sid ||
                    cookieUtils.getItem(baseConfig.LAST_PROFILE_ID)
            },
            {
                fetchPolicy: 'cache-and-network'
            }
        );
        onFreeHeatmapResult(data => {
            if (data?.data?.getFreeProfileHeatMapUrl !== undefined) {
                freeHeatmapUrl.value = data?.data?.getFreeProfileHeatMapUrl;
                resolveCallback &&
                    typeof resolveCallback === 'function' &&
                    resolveCallback(freeHeatmapUrl.value);
                freeHeatmapFetchLoading.value = false;
            }
        });
        onFreeHeatmapError(err => {
            freeHeatmapUrl.value = '';
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback();
            freeHeatmapFetchLoading.value = false;
        });
    };

    return {
        loading,
        topHeatmapList,
        getTopHeatmapList,
        freeHeatmapUrl,
        freeHeatmapFetchLoading,
        getFreeHeatmapUrl
    };
}
